import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ResetSelectionLoadedEvent, SelectionLoadedEvent} from "../event/ContentEvents";
import {PauseSelectionEvent} from "../event/ReadAndRecordEvents";
import {TAI_STATUS, TaiEvent} from "../event/TaiEvent";
import {CssSelection, CssText, CssWord} from "./content-view.objects";
import {ClientConfiguration} from "../services/configuration/client.config";
import {AllowedSelectionType, ContentCursor} from "../services/content/content.cursor";
import {ReadToMeService} from "../services/reading/readtome.service";
import {ComponentBase} from "../base/ComponentBase";
import {EventBus} from "../event/EventBus";
import {ScrollService} from "../services/scroll/scroll.service";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {QuestionState} from "../services/usermodel/usermodel.objects";
import {QuestionService} from "../services/question/question.service";
import {filter} from "rxjs/operators";

@Component({
    providers: [],
    selector: "content-view",
    templateUrl:"./content-view.component.html",
    styleUrls:["./content-view.component.scss"]
})

export class ContentViewComponent extends ComponentBase implements OnInit, AfterViewInit {

    @Output()
    public taiSelected = new EventEmitter();

    @Output()
    public userSelectedWord = new EventEmitter();

    @Output()
    public scrollArrowClicked = new EventEmitter();

    @Input()
    set scrollArrowVisible(value: boolean) {
        this.scrollArrow = value;
    }
    get content(): CssSelection {
        return this.contentCursor.selection;
    }

    private startingContentTop: number = 0;
    private selectionTitleLoaded: string = "";
    public selectionStyle: any = {"top": "0px"};
    public loadingOn: boolean = true;
    public taiQuestionStates: {};
    public scrollArrow: boolean = false;

    constructor(private config: ClientConfiguration,
                private contentCursor: ContentCursor,
                private eventBus: EventBus,
                private scrollService: ScrollService,
                private readToMeService: ReadToMeService,
                private userModelService: UserModelService,
                private questionService: QuestionService,
                private log: BugsnagLoggingService,
                private changeDetectorRef: ChangeDetectorRef) {
        super("ContentViewComponent");
        this.reloadTAIQuestionStates();
    }

    ngOnInit(): void {
        this.log.log(`Init component (override): ${this.componentName}  ${this.uuid}`);

        let taiSubscribe = this.eventBus.pipe(filter(e => e instanceof TaiEvent)).subscribe((e: TaiEvent) => {
            if (e.status === TAI_STATUS.end_tai) {
                this.reloadTAIQuestionStates();
            }
        });

        let selectionSubscribe = this.eventBus.pipe(filter(e => e instanceof SelectionLoadedEvent))
            .subscribe((event: SelectionLoadedEvent) => {
                if (event.type === SelectionLoadedEvent.TYPE && this.selectionTitleLoaded !== event.name) {
                    this.selectionLoad(event.name);
            }
        });

        let resetSubscribe = this.eventBus.subscribe((event: ResetSelectionLoadedEvent) => {
            if (event.type === ResetSelectionLoadedEvent.TYPE) {
                this.selectionTitleLoaded = "";
                this.resetContentViewState();
            }
        });

        let pauseSubscribe = this.eventBus.subscribe((event: PauseSelectionEvent) => {
            this.resetContentScrollTop();
        });

        this.subscriptions.push(taiSubscribe);
        this.subscriptions.push(selectionSubscribe);
        this.subscriptions.push(resetSubscribe);
        this.subscriptions.push(pauseSubscribe);
    }

    ngAfterViewInit(): void {
        if (this.contentCursor.selection && this.contentCursor.selection.metaData
            && this.contentCursor.selection.metaData.referenceStory) {
            this.selectionLoad(this.contentCursor.selection.metaData.referenceStory);
        }
    }

    private resetContentViewState(): void {
        this.selectionStyle = {"top": "0px"};
        this.startingContentTop = 0;
        this.reloadTAIQuestionStates();
    }

    private selectionLoad(selectionName: string): void {
        this.selectionTitleLoaded = selectionName;
        this.resetContentViewState();
        this.adjustTopBuffer();
    }

    private resetContentScrollTop(): void {
        if (!!this.startingContentTop) {
            this.selectionStyle = { "top": this.startingContentTop.toString() + "px" };
        }
    }

    private reloadTAIQuestionStates(): void {
        this.taiQuestionStates = this.userModelService.thinkAboutQuestionStatesMap;
    }

    private adjustTopBuffer(): void {
        let selectionTitle = document.getElementById("selection-title");
        let selectionContent = document.getElementById("selection-content");
        let collideInfo = this.scrollService.getVerticalCollideInfo(selectionTitle, selectionContent);
        let bufferDifference = collideInfo.buffer + this.startingContentTop;

        if (collideInfo.isColliding && this.startingContentTop === 0) {
            this.selectionStyle = { "top": collideInfo.buffer.toString() + "px" };
            this.startingContentTop = collideInfo.buffer;
        } else if (this.startingContentTop !== 0) {
            this.selectionStyle = { "top": bufferDifference.toString() + "px" };
            this.startingContentTop = bufferDifference;
        }
    }

    // noinspection JSUnusedLocalSymbols
    public selectTAI(questionId: string): void {
        // Should we go through ContentCursor here as well?
        this.taiSelected.emit({id: questionId});
    }

    // noinspection JSUnusedLocalSymbols
    public selectWord(word: CssWord): void {
        // make sure a click is a complete no-op if that is what is intended, otherwise the
        // pause below has side effects.
        if (this.contentCursor.getAllowedSelectionType() === AllowedSelectionType.NONE) {
            return;
        }
        this.readToMeService.pause();
        this.contentCursor.userSelectWord(word);
        this.changeDetectorRef.detectChanges();
    }

    public selectImage(imageSrc:CssText): void {
        this.contentCursor.selectText(imageSrc);
    }

    public getImageURL(imgSrc: string): string {
        return this.config.selectionImageUrl(imgSrc);
    }

    public getTAIQuestionState(questionId: string): {"done": boolean} {
        let index = this.questionService.retrieveQuestionIndex(this.questionService.retrieveQuestion(questionId));
        let activeState = this.taiQuestionStates[index];
        let answeredCorrectly = activeState === QuestionState.ANSWERED_CORRECTLY ||
                                activeState === QuestionState.PREVIOUSLY_ANSWERED_CORRECTLY;
        return { "done": answeredCorrectly };
    }

    public scrollArrowClickedEvent(): void {
        this.scrollArrowClicked.emit();
    }
}
